import React, { useEffect, useRef, useState } from 'react';
import { formatShortTimestamp } from '../../utils';

export const NotificationsBell = ({
  notifications = [],
  unreadCount = 0,
  onOpen,
  onNotificationClick,
  onClear,
  onTrackAlert,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const handleToggle = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    if (nextOpen && unreadCount > 0) {
      onOpen?.();
    }
  };

  const handleNotificationSelect = (notification) => {
    onNotificationClick?.(notification);
    setIsOpen(false);
  };

  return (
    <div className={`notifications ${isOpen ? 'open' : ''}`} ref={panelRef}>
      <button
        type="button"
        className={`notification-button ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={handleToggle}
        aria-label={`Notificaciones ${unreadCount > 0 ? `(${unreadCount} nuevas)` : ''}`}
        aria-expanded={isOpen}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 17h5l-1.4-1.4a2 2 0 0 1-.6-1.4V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
          <path d="M10 17a2 2 0 0 0 4 0" />
        </svg>
        {unreadCount > 0 && <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <div>
              <div className="notification-panel-title">Alertas</div>
              <div className="notification-panel-subtitle">
                {notifications.length > 0 ? `${notifications.length} registradas` : 'Sin alertas activas'}
              </div>
            </div>
            {notifications.length > 0 && onClear && (
              <button
                type="button"
                className="notification-clear-btn"
                onClick={() => { onClear(); setIsOpen(false); }}
                title="Limpiar todas las notificaciones"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" />
                  <path d="M8 6V4h8v2" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
                Limpiar
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <div className="notification-empty-title">Todo en rango</div>
                <div className="notification-empty-text">No hay piscinas con alertas recientes.</div>
              </div>
            ) : (
              notifications.map((notification) => (
                <div key={notification.id} className={`notification-item ${notification.level}`}>
                  <button
                    type="button"
                    className="notification-item-inner"
                    onClick={() => handleNotificationSelect(notification)}
                  >
                    <div className="notification-item-top">
                      <span className="notification-room">{notification.roomCode}</span>
                      <span className={`notification-level ${notification.level}`}>
                        {notification.level === 'critical' ? 'Critica' : 'Advertencia'}
                      </span>
                    </div>
                    <div className="notification-sensor">{notification.sensorLabel}</div>
                    <div className="notification-value">{notification.valueLabel}</div>
                    <div className="notification-threshold">{notification.thresholdDetail}</div>
                    <div className="notification-action">{notification.action}</div>
                    <div className="notification-time">{formatShortTimestamp(notification.createdAt)}</div>
                  </button>
                  {notification.level === 'critical' && onTrackAlert && (
                    <button
                      type="button"
                      className="notification-track-btn"
                      onClick={(e) => { e.stopPropagation(); onTrackAlert(notification); setIsOpen(false); }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      Dar seguimiento
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
